/**
 * Entry point for development and production PWA builds.
 */
import 'regenerator-runtime/runtime';
import App from './App';
import React from 'react';
import ReactDOM from 'react-dom';
import { history } from './utils/history';

/**
 * EXTENSIONS AND MODES
 * =================
 * pluginImports.js is dynamically generated from extension and mode
 * configuration at build time.
 *
 * pluginImports.js imports all of the modes and extensions and adds them
 * to the window for processing.
 */
import { modes as defaultModes, extensions as defaultExtensions } from './pluginImports';
import loadDynamicConfig from './loadDynamicConfig';
import CustomApp from './customApp';
import { preloadUserMouseActions } from './utils/mouseActionsApi';
import { preloadPanelPreference } from './utils/panelPreferenceApi';
import { preloadFavoriteTools } from './utils/favoriteToolsApi';
import { preloadUserHotkeys } from './utils/hotkeysApi';
import { preloadWorkstationLayout } from './utils/workstationLayout';
import { preloadReportPreview } from './utils/reportPreviewPref';
import { preloadReportingPanels } from './utils/reportingPanelPref';
import { startViewerSlotHeartbeat } from './utils/viewerSlotRegistry';
import { preloadPacsTheme } from './utils/pacsTheme';

// If this window is a viewer occupying a buffer slot, start announcing that it is alive
// straight away — before the preference round-trips below, which a cold backend can drag
// out. Until the first beat lands, the slot is protected only by the registry's boot
// grace period, and another window could otherwise reuse this one's name. A no-op in
// every window that is not a buffered viewer.
startViewerSlotHeartbeat();

loadDynamicConfig(window.config).then(async config_json => {
  // Reset Dynamic config if defined
  if (config_json !== null) {
    window.config = config_json;
  }

  /**
   * Combine our appConfiguration with installed extensions and modes.
   * In the future appConfiguration may contain modes added at runtime.
   *  */
  const appProps = {
    config: window ? window.config : {},
    defaultExtensions,
    defaultModes,
  };

  // Hydrate per-user mouse-click actions from backend into localStorage BEFORE
  // any viewer / tool-group code (which reads localStorage synchronously) mounts.
  // Safe no-op when not logged in.
  await preloadUserMouseActions();
  await preloadPanelPreference();
  await preloadFavoriteTools();
  await preloadUserHotkeys();
  // Dual-monitor layout is read synchronously at click time (right before window.open),
  // so it has to be in localStorage before any table can be clicked.
  await preloadWorkstationLayout();
  // Post-commit report preview is read synchronously the moment a report is committed,
  // so it has to be in localStorage before the reporting page can reach that point.
  await preloadReportPreview();
  // Reporting study-queue rail state is read synchronously at the reporting page's first
  // render, so it has to be in localStorage before that route can mount.
  await preloadReportingPanels();
  // PACS List theme — hydrated before mount so a dark-mode user's first paint is
  // already dark instead of flashing the white worklist.
  await preloadPacsTheme();

  /** Create App */
  // const app = React.createElement(App, appProps, null);
  // const pacs_app_element = document.getElementById('pacs-app');
  // if(pacs_app_element) {
  //   ReactDOM.render(app, pacs_app_element);
  // }

  const customApp = React.createElement(CustomApp, appProps, null);

  window.volumeLoadInfo = {};
  window.seriesLoadInfo = {};

  // const root = document.getElementById('root');
  /** Render */
  ReactDOM.render(customApp, document.getElementById('custom-root'));
});

export { history };
